import React from "react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useTheme } from "@/context/ThemeContext";
import { Zap, Menu, X, Heart, Sun, Moon, MessageSquare } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { BrandMark } from "@/components/BrandMark";
import { SiteNavLink } from "@/components/SiteNavLink";
import { SITE_CONTAINER } from "@/lib/siteLayout";

export const Navbar = () => {
  const { content } = useSiteContent();
  const navLinks = (content.nav?.links || []).filter((l) => l.visible !== false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const chat = useChat();
  const favorites = useFavorites();
  const { theme, toggle: toggleTheme } = useTheme();
  const totalUnread = chat?.totalUnread || 0;
  const favCount = favorites?.count || 0;
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong border-b border-white/10 py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className={`${SITE_CONTAINER} flex items-center justify-between`}>
        <Link to="/" data-testid="logo" className="flex items-center gap-2 group">
          <Zap className="w-6 h-6 text-electric group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <span className="font-heading text-xl font-semibold tracking-tight">
            <BrandMark />
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <SiteNavLink
              key={`${l.path}-${l.label}`}
              link={l}
              className="text-sm text-slate-300 hover:text-white transition-colors"
            />
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            title={theme === "dark" ? "Világos téma" : "Sötét téma"}
            aria-label="Téma váltása"
            className="relative w-10 h-10 rounded-full glass hover:bg-white/10 flex items-center justify-center transition-all"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4 text-amber-300" strokeWidth={1.75} />
            ) : (
              <Moon className="w-4 h-4 text-electric" strokeWidth={1.75} />
            )}
          </button>
          <Link
            to="/kedvencek"
            data-testid="nav-favorites"
            title="Kedvenceim"
            className="relative w-10 h-10 rounded-full glass hover:bg-white/10 flex items-center justify-center transition-all"
          >
            <Heart className={`w-4 h-4 ${favCount > 0 ? "text-red-400" : "text-slate-300"}`} fill={favCount > 0 ? "currentColor" : "none"} strokeWidth={1.5} />
            {favCount > 0 && (
              <span data-testid="nav-favorites-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                {favCount}
              </span>
            )}
          </Link>
          {user ? (
            <>
              <button
                type="button"
                data-testid="nav-messages"
                title="Üzenetek"
                onClick={() => chat.openInbox?.()}
                className="relative w-10 h-10 rounded-full glass hover:bg-white/10 flex items-center justify-center transition-all"
              >
                <MessageSquare className={`w-4 h-4 ${totalUnread > 0 ? "text-electric" : "text-slate-300"}`} strokeWidth={1.5} />
                {totalUnread > 0 && (
                  <span data-testid="nav-unread" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                    {totalUnread > 99 ? "99+" : totalUnread}
                  </span>
                )}
              </button>
              <button
                data-testid="nav-account"
                onClick={() => navigate("/fiok")}
                className="px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all hover:blue-glow"
              >
                Fiókom
              </button>
            </>
          ) : (
            <button
              data-testid="nav-login"
              onClick={() => navigate("/belepes")}
              className="px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all hover:blue-glow"
            >
              Belépés / Eladás
            </button>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <button
            data-testid="theme-toggle-mobile"
            onClick={toggleTheme}
            aria-label="Téma váltása"
            className="w-9 h-9 rounded-full glass flex items-center justify-center"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-amber-300" strokeWidth={1.75} /> : <Moon className="w-4 h-4 text-electric" strokeWidth={1.75} />}
          </button>
          <button className="text-white" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass-strong mt-3 mx-4 rounded-2xl p-6 flex flex-col gap-4">
          {navLinks.map((l) => (
            <SiteNavLink
              key={`m-${l.path}-${l.label}`}
              link={l}
              onClick={() => setOpen(false)}
              className="text-slate-200 hover:text-electric"
            />
          ))}
          <Link to="/kedvencek" data-testid="nav-favorites-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-200 hover:text-electric">
            <Heart className="w-4 h-4" strokeWidth={1.5} /> Kedvenceim{favCount > 0 ? ` (${favCount})` : ""}
          </Link>
          {user && (
            <button
              type="button"
              data-testid="nav-messages-mobile"
              onClick={() => { chat.openInbox?.(); setOpen(false); }}
              className="flex items-center gap-2 text-slate-200 hover:text-electric text-left"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
              Üzenetek{totalUnread > 0 ? ` (${totalUnread})` : ""}
            </button>
          )}
          <button onClick={() => navigate(user ? "/fiok" : "/belepes")} className="text-left text-electric font-medium">
            {user ? "Fiókom" : "Belépés / Eladás"}
          </button>
        </div>
      )}
    </nav>
  );
};
